import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { authApi } from '@/services/api';

// Import socket disconnect function dynamically to avoid circular dependency
let disconnectSocketFn: (() => void) | null = null;
export function setDisconnectSocketFn(fn: () => void) {
  disconnectSocketFn = fn;
}

interface User {
  id: string;
  email: string;
  username: string;
  displayName: string | null;
  userType: string;
  role?: string;
  avatarUrl: string | null;
  isPremium: boolean;
  bio?: string | null;
  country?: string | null;
  website?: string | null;
  referralCode?: string | null;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isHydrated: boolean;
  login: (user: User, accessToken: string, refreshToken: string) => void;
  register: (data: { email: string; password: string; username: string; displayName?: string }) => Promise<void>;
  logout: () => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  fetchCurrentUser: () => Promise<void>;
  updateUser: (userData: Partial<User>) => void;
  setHydrated: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      isHydrated: false,

      login: (user, accessToken, refreshToken) => {
        set({
          user,
          accessToken,
          refreshToken,
          isAuthenticated: true,
          isLoading: false,
        });
      },

      register: async (data) => {
        set({ isLoading: true });
        try {
          const { data: response } = await authApi.register(data);
          set({
            user: response.data.user,
            accessToken: response.data.accessToken,
            refreshToken: response.data.refreshToken,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      logout: () => {
        const { refreshToken } = get();
        if (refreshToken) {
          authApi.logout(refreshToken).catch(() => {});
        }
        // Disconnect the singleton socket (if function is registered)
        if (disconnectSocketFn) {
          disconnectSocketFn();
        }
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
        });
        // Also clear any localStorage items that might persist
        localStorage.removeItem('sessionId');
      },

      setTokens: (accessToken, refreshToken) => {
        set({ accessToken, refreshToken });
      },

      fetchCurrentUser: async () => {
        const { accessToken } = get();
        if (!accessToken) {
          set({ user: null, isAuthenticated: false });
          return;
        }
        try {
          const { data } = await authApi.getCurrentUser();
          set({ user: data.data.user, isAuthenticated: true });
        } catch {
          // Don't automatically logout on fetch failure - let the interceptor handle 401s
          console.warn('Failed to fetch current user');
        }
      },

      updateUser: (userData) => {
        set((state) => ({
          user: state.user ? { ...state.user, ...userData } : null,
        }));
      },

      setHydrated: () => {
        set({ isHydrated: true });
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        // Mark as hydrated once rehydration is complete
        state?.setHydrated();
      },
    }
  )
);
